import React from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Button, Text, Box, Flex, VStack, SimpleGrid, Divider, Heading } from "@chakra-ui/react";
import PageContainer from "../../components/pageContainer/PageContainer";
import PageBanner from "../../components/pageBanner/PageBanner";
import "./VerifyResult.css";

function VerifyResult() {
  const { state } = useLocation();
  const routerHistory = useHistory();

  if (!state) {
    return (
      <PageContainer>
        <PageBanner />
        <div className="verify-result-container">
          <Text color="red.500" mt={10}>
            No verification data found. Please verify again.
          </Text>
          <Button mt={5} colorScheme="blue" onClick={() => routerHistory.push("/verify")}>
            Back to Verify
          </Button>
        </div>
      </PageContainer>
    );
  }

  // Extract request parameters (passed from Verify page)
  const { body, certificateId, error } = state;
  const {
    fieldData,
    sector,
    sectorName,
    productionName,
    year,
    batchNumber,
    blockchainVerification,
    scanHistory,
  } = body || {};

  const filteredScanHistory = scanHistory.filter((scan) => scan.scanType !== "PUBLIC");

  if (error) {
    return (
      <PageContainer>
        <PageBanner />
        <div className="verify-result-container">
          <div className="verify-result-card error-card">
            <Text className="verify-result-title error-title">Verification Failed</Text>

            <div className="verify-result-details">
              <Text textAlign="center">{error}</Text>
            </div>

            <div className="verify-result-actions">
              <Button colorScheme="blue" onClick={() => routerHistory.push("/verify")}>
                Back to Verify Page
              </Button>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  // Helper function to format field names for display
  const formatFieldName = (fieldName) => {
    return fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <PageContainer>
      <PageBanner />
      <VStack spacing={6} p={6} align="stretch" maxW="1200px" margin="0 auto">
        <Box textAlign="center" mb={2}>
          <Heading as="h1" size="xl" color="green.500" mb={2}>
            Verification Successful
          </Heading>
          <Text color="gray.600">This certificate is authentic and verifiable.</Text>
        </Box>

        {/* Blockchain Verification Badge */}
        {blockchainVerification && blockchainVerification.checked && (
          <Flex
            align="center"
            gap={4}
            p={4}
            borderRadius="lg"
            bg={blockchainVerification.verified ? "green.50" : "orange.50"}
            border="1px solid"
            borderColor={blockchainVerification.verified ? "green.200" : "orange.200"}
            boxShadow="sm"
          >
            <Flex
              align="center"
              justify="center"
              w="40px"
              h="40px"
              borderRadius="full"
              bg={blockchainVerification.verified ? "green.100" : "orange.100"}
              color={blockchainVerification.verified ? "green.600" : "orange.600"}
            >
              <Text fontSize="xl" fontWeight="black">
                {blockchainVerification.verified ? "✓" : "⚠"}
              </Text>
            </Flex>
            <Box>
              <Text
                fontWeight="bold"
                fontSize="md"
                color={blockchainVerification.verified ? "green.800" : "orange.800"}
              >
                {blockchainVerification.verified
                  ? "BlockForest Verified"
                  : "BlockForest Verification Failed"}
              </Text>
              <Text
                fontSize="sm"
                color={blockchainVerification.verified ? "green.700" : "orange.700"}
              >
                {blockchainVerification.verified
                  ? "This certificate is registered and actively verified in BlockForest."
                  : "This certificate could not be verified in BlockForest."}
              </Text>
            </Box>
          </Flex>
        )}

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} align="start">
          <VStack spacing={6} align="stretch">
            {/* Main Details Card */}
            <Box
              bg="white"
              borderRadius="lg"
              border="1px solid #e2e8f0"
              boxShadow="sm"
              overflow="hidden"
            >
              <Box bg="gray.50" px={6} py={4} borderBottom="1px solid #e2e8f0">
                <Heading as="h3" size="md" color="gray.700">
                  Main Details
                </Heading>
              </Box>
              <Box p={6}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color="gray.500"
                      textTransform="uppercase"
                      letterSpacing="wide"
                    >
                      Production Name
                    </Text>
                    <Text fontSize="md" color="gray.800" fontWeight="medium">
                      {productionName || "Unknown"}
                    </Text>
                  </Box>
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color="gray.500"
                      textTransform="uppercase"
                      letterSpacing="wide"
                    >
                      Certificate ID
                    </Text>
                    <Text fontSize="md" color="gray.800" fontWeight="medium">
                      {certificateId || "Unknown"}
                    </Text>
                  </Box>
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color="gray.500"
                      textTransform="uppercase"
                      letterSpacing="wide"
                    >
                      Batch Number
                    </Text>
                    <Text fontSize="md" color="gray.800" fontWeight="medium">
                      {batchNumber || "Unknown"}
                    </Text>
                  </Box>
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color="gray.500"
                      textTransform="uppercase"
                      letterSpacing="wide"
                    >
                      Sector
                    </Text>
                    <Text fontSize="md" color="gray.800" fontWeight="medium">
                      {sectorName || sector || "Unknown"}
                    </Text>
                  </Box>
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color="gray.500"
                      textTransform="uppercase"
                      letterSpacing="wide"
                    >
                      Year
                    </Text>
                    <Text fontSize="md" color="gray.800" fontWeight="medium">
                      {year || "Unknown"}
                    </Text>
                  </Box>
                  {(sector === "MED" || body?.certificateType) && (
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        color="gray.500"
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        Certificate Type
                      </Text>
                      <Text fontSize="md" color="gray.800" fontWeight="medium">
                        {body?.certificateType
                          ? body.certificateType.charAt(0).toUpperCase() +
                            body.certificateType.slice(1)
                          : "Unknown"}
                      </Text>
                    </Box>
                  )}
                </SimpleGrid>
              </Box>
            </Box>

            {/* Certificate Details Card */}
            {fieldData && Object.keys(fieldData).length > 0 && (
              <Box
                bg="white"
                borderRadius="lg"
                border="1px solid #e2e8f0"
                boxShadow="sm"
                overflow="hidden"
              >
                <Box bg="gray.50" px={6} py={4} borderBottom="1px solid #e2e8f0">
                  <Heading as="h3" size="md" color="gray.700">
                    Certificate Details
                  </Heading>
                </Box>
                <Box p={6}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    {Object.entries(fieldData)
                      .filter(([_, fieldValue]) => {
                        if (
                          fieldValue &&
                          typeof fieldValue === "object" &&
                          "visible" in fieldValue
                        ) {
                          return fieldValue.visible === true;
                        }
                        return true;
                      })
                      .map(([key, fieldValue]) => {
                        const displayValue =
                          typeof fieldValue === "object" &&
                          fieldValue !== null &&
                          "value" in fieldValue
                            ? fieldValue.value
                            : fieldValue;
                        return (
                          <Box key={key} py={2}>
                            <Text
                              fontSize="xs"
                              fontWeight="bold"
                              color="gray.500"
                              textTransform="uppercase"
                              letterSpacing="wide"
                            >
                              {formatFieldName(key)}
                            </Text>
                            <Text fontSize="md" color="gray.800" fontWeight="medium">
                              {displayValue || "N/A"}
                            </Text>
                          </Box>
                        );
                      })}
                  </SimpleGrid>
                </Box>
              </Box>
            )}
          </VStack>

          {/* Scan History Timeline Card */}
          {filteredScanHistory && filteredScanHistory.length > 0 && (
            <Box
              bg="white"
              borderRadius="lg"
              border="1px solid #e2e8f0"
              boxShadow="sm"
              overflow="hidden"
              height="fit-content"
            >
              <Box bg="gray.50" px={6} py={4} borderBottom="1px solid #e2e8f0">
                <Heading as="h3" size="md" color="gray.700">
                  Verification History
                </Heading>
              </Box>
              <Box p={8} maxH="820px" overflowY="auto" className="custom-scrollbar">
                <Box
                  ml={4}
                  borderLeft="2px solid"
                  borderColor="blue.300"
                  pl={8}
                  position="relative"
                >
                  {[...filteredScanHistory]
                    .sort((a, b) => new Date(b.scanTime) - new Date(a.scanTime))
                    .map((scan, index) => (
                      <Box key={index} mb={8} position="relative">
                        {/* Timeline Dot */}
                        <Box
                          position="absolute"
                          left="-41px"
                          top="4px"
                          width="16px"
                          height="16px"
                          bg="blue.500"
                          border="3px solid white"
                          borderRadius="50%"
                          boxShadow="0 0 0 2px var(--chakra-colors-blue-200)"
                        />

                        {/* Content Card */}
                        <Box
                          bg="gray.50"
                          p={4}
                          borderRadius="md"
                          border="1px solid"
                          borderColor="gray.100"
                          _hover={{ boxShadow: "md", borderColor: "blue.200" }}
                          transition="all 0.2s"
                        >
                          <Flex justify="space-between" align="center" mb={2} gap={4}>
                            <Text fontWeight="bold" fontSize="md" color="gray.800">
                              {scan.location || "Unknown Location"}
                            </Text>
                            <Text
                              fontSize="xs"
                              fontWeight="bold"
                              color="gray.500"
                              bg="gray.200"
                              px={2}
                              py={1}
                              borderRadius="full"
                            >
                              {new Date(scan.scanTime).toLocaleString([], {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })}
                            </Text>
                          </Flex>

                          <Flex gap={4} mt={3}>
                            <Box flex="1">
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                textTransform="uppercase"
                                letterSpacing="wide"
                              >
                                Account Type
                              </Text>
                              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                                {scan.accountType}
                              </Text>
                            </Box>
                            <Box flex="1">
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                textTransform="uppercase"
                                letterSpacing="wide"
                              >
                                Action
                              </Text>
                              <Text fontSize="sm" fontWeight="semibold" color="blue.600">
                                {scan.scanType}
                              </Text>
                            </Box>
                            <Box flex="1">
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                textTransform="uppercase"
                                letterSpacing="wide"
                              >
                                Company Name
                              </Text>
                              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                                {scan.companyName}
                              </Text>
                            </Box>
                          </Flex>
                        </Box>
                      </Box>
                    ))}
                </Box>
              </Box>
            </Box>
          )}
        </SimpleGrid>

        <Flex justify="center" mt={4}>
          <Button size="lg" colorScheme="blue" px={8} onClick={() => routerHistory.push("/verify")}>
            Back to Verify Page
          </Button>
        </Flex>
      </VStack>
    </PageContainer>
  );
}

export default VerifyResult;
